import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { holidaysAPI, branchesAPI, QUERY_KEYS } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { BranchFilterSelect, LoadingSpinner, Pagination } from '@/components/common';
import { CalendarDays, Plus, Trash2, Gift } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { paginateClientData } from '@/utils/pagination';

export default function HolidaysPage() {
  const perPage = 9;
  const { can } = useAuth();
  const canManage = can('manage_holidays');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [branchFilter, setBranchFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: holidaysData, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.holidays(), selectedYear, branchFilter],
    queryFn: () => holidaysAPI.list({ year: selectedYear, branch_id: branchFilter }),
  });

  const { data: branchesData } = useQuery({
    queryKey: QUERY_KEYS.branches(),
    queryFn: () => branchesAPI.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => holidaysAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.holidays() });
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => holidaysAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.holidays() });
    },
  });

  const holidays = Array.isArray(holidaysData?.data)
    ? holidaysData.data
    : holidaysData?.data?.data || [];
  const branches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : branchesData?.data?.data || [];

  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этот праздник?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  // Group holidays by month
  const { items: visibleHolidays, meta } = paginateClientData<any>(holidays, page, perPage);

  const groupedHolidays = visibleHolidays.reduce((acc: any, holiday: any) => {
    const holidayDate = holiday.date || holiday.holiday_date;
    const month = new Date(holidayDate).toLocaleString('ru-RU', { month: 'long' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(holiday);
    return acc;
  }, {});

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Праздники</h1>
        {canManage && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Добавить праздник
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title="Новый праздник"
      >
        <HolidayForm
          branches={branches}
          onSubmit={async (data) => {
            await createMutation.mutateAsync(data);
          }}
          onCancel={() => setShowForm(false)}
        />
      </Modal>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <select
              className="px-4 py-2 border rounded-lg"
              value={selectedYear}
              onChange={(e) => {
                setPage(1);
                setSelectedYear(e.target.value);
              }}
            >
              {[2024, 2025, 2026, 2027].map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <BranchFilterSelect
              alwaysShow
              className="px-4 py-2 border rounded-lg flex-1"
              value={branchFilter}
              onChange={(value) => {
                setPage(1);
                setBranchFilter(value);
              }}
            />
          </div>
        </CardContent>
      </Card>
      {Object.keys(groupedHolidays).length > 0 ? (
        <div className="space-y-6 page-shell">
          {Object.entries(groupedHolidays).map(([month, monthHolidays]: [string, any]) => (
            <Card key={month}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  {month} {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {monthHolidays.map((holiday: any) => (
                    <div
                      key={holiday.id}
                      className="flex flex-col gap-3 rounded-lg bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                          <Gift className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{holiday.name}</h4>
                          <p className="text-sm text-gray-500">
                            {new Date(holiday.date || holiday.holiday_date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'short',
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <Badge variant={holiday.is_paid ? 'success' : 'secondary'}>
                          {holiday.is_paid ? 'Оплачиваемый' : 'Неоплачиваемый'}
                        </Badge>
                        {holiday.branch && (
                          <span className="text-sm text-gray-500">{holiday.branch.name}</span>
                        )}
                        {canManage && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(holiday.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <CalendarDays className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Праздники не найдены</h3>
          <p className="text-gray-500">Добавьте праздники для исключения из расчетов посещаемости.</p>
        </div>
      )}
      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  );
}

function HolidayForm({
  branches,
  onSubmit,
  onCancel,
}: {
  branches: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    branch_id: '',
    is_paid: true,
  });

  return (
    <div className="space-y-4">
      <Input
        placeholder="Название праздника (например, Новый год, День независимости)"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          type="date"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
        />
        <select
          className="px-4 py-2 border rounded-lg"
          value={formData.branch_id}
          onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
        >
          <option value="">Все филиалы</option>
          {branches.map((branch: any) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </select>
      </div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.is_paid}
          onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
        />
        Оплачиваемый праздник
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={() => onSubmit(formData)} className="flex-1">
          Сохранить
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Отмена
        </Button>
      </div>
    </div>
  );
}

